import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Moon, Heart, Lightbulb, Clock, Smartphone, BookOpen, ArrowRight, Star } from "lucide-react";
import { LIGHT_COLORS, BENEFITS, HOW_IT_WORKS } from "../lib/content";
import { Reveal, EASE } from "./motion";
import CompareSection from "./CompareSection";

const Eyebrow = ({ children, dark = false }) => (
  <p className={`flex items-center gap-2 text-xs font-bold uppercase tracking-[0.3em] ${dark ? "text-moon" : "text-pinkdeep"}`}>
    <Moon size={13} /> {children}
  </p>
);

const PARENT_CARDS = [
  { num: "01", title: "Why bedtime can feel hard", bg: "#FBE7EF",
    body: "When the lights go out, everything gets quieter — and little imaginations get louder. Strange shadows, unfamiliar sounds, worries from the day, and missing Mom or Dad can all show up at once. You're not doing anything wrong. Bedtime is genuinely a big moment for small people." },
  { num: "02", title: "Creating a familiar routine", bg: "#EEE8FF",
    body: "Kids relax into what they can predict. A simple, repeatable sequence — bath, pajamas, teeth, story, Buddy, lights-down — gives them something steady to hold onto. The routine itself becomes the comfort." },
  { num: "03", title: "How the Buddy fits in", bg: "#DFF3FF",
    body: "A Bedtime Buddy becomes the part of the routine that stays after you leave. Something soft to hug, a familiar voice telling a story, and a gentle glow beside them — the same every night, in their bed, on trips, at sleepovers." },
  { num: "04", title: "Safety, plainly", bg: "#FFF3D1",
    body: "Bedtime Buddies are comfort companions, not baby products. They're not intended for infant sleep environments, and every Buddy ships with clear age and use guidelines. Full details live on our Safety page." },
  { num: "05", title: "Cleaning and care", bg: "#E4F6E9",
    body: "Life with kids is sticky. The plush outer is designed for surface cleaning, and the electronics module is protected inside. Full care instructions ship with every Buddy." },
];

const STORY_CARDS = [
  { title: "Luna and the Cloud Castle", img: "story_luna", color: "#FBE7EF", desc: "A magical journey above the clouds where dreams find a home." },
  { title: "Freddy's Sleepy Pond", img: "story_freddy", color: "#E4F6E9", desc: "A gentle adventure about finding peace, one ripple at a time." },
  { title: "Poppy Finds Her Brave", img: "story_poppy", color: "#FBE7EF", desc: "A heartwarming story about courage, even when it feels small." },
  { title: "Hugo and the Shooting Star", img: "story_hugo", color: "#EEE8FF", desc: "A twinkling tale about wishes, friendship, and what's possible." },
];

function NightLightSection() {
  const [light, setLight] = useState(0);
  const [brightness, setBrightness] = useState(60);
  const activeLight = LIGHT_COLORS[light];

  const glowStrength = 0.3 + (brightness / 100) * 0.6;
  const glowAlpha = Math.round(glowStrength * 255).toString(16).padStart(2, "0");
  const glowBlur = Math.round(14 + brightness * 0.6);
  const glowSpread = Math.round(2 + brightness * 0.1);
  const glowBase = typeof activeLight.hex === "string" && activeLight.hex.startsWith("#") ? activeLight.hex : "#F58CC8";

  return (
    <>
      {/* Night light */}
      <section className="relative overflow-hidden px-6 py-20 lg:py-28" data-testid="night-light-section">
        <Star className="absolute left-10 top-14 h-5 w-5 text-pink" fill="#F58CC8" aria-hidden="true" />
        <Star className="absolute right-16 top-10 h-6 w-6 text-lavdeep" aria-hidden="true" />
        <Moon className="absolute right-8 top-32 h-10 w-10 rotate-[20deg] text-blush" fill="#FBE7EF" aria-hidden="true" />
        <Star className="absolute left-6 bottom-16 h-6 w-6 text-lavdeep" fill="#EEE8FF" aria-hidden="true" />
        <div className="mx-auto grid max-w-6xl items-center gap-14 lg:grid-cols-2">
          <Reveal className="order-2">
            <div className="relative mx-auto max-w-xl">
              <div
                className="relative rounded-[2.5rem] p-1.5 transition-all duration-500"
                style={{
                  background: activeLight.hex,
                  boxShadow: `0 0 ${glowBlur}px ${glowSpread}px ${glowBase}${glowAlpha}, 0 24px 70px rgba(16,42,86,0.14)`,
                }}
                data-testid="glow-frame">
                <div className="overflow-hidden rounded-[2.1rem]">
                  <img src="/assets/nightlight-art.png" alt="Little girl cuddling Luna the Unicorn as her belly glows warmly beside them"
                    className="w-full object-cover" />
                </div>
              </div>
              <div className="relative z-10 -mt-16 mx-auto w-[88%] rounded-[1.75rem] bg-white/95 p-6 shadow-[0_16px_50px_rgba(16,42,86,0.14)] backdrop-blur">
                <div className="flex justify-center gap-3.5" role="group" aria-label="Light colors">
                  {LIGHT_COLORS.map((c, i) => (
                    <button key={c.name} onClick={() => setLight(i)} aria-label={c.name} data-testid={`light-color-${i}`}
                      className={`h-10 w-10 rounded-full transition hover:scale-110 ${light === i ? "ring-2 ring-navy ring-offset-2 scale-110" : ""}`}
                      style={{ background: c.hex }} />
                  ))}
                </div>
                <div className="mt-5 flex justify-between text-xs font-bold uppercase tracking-[0.2em] text-navy/45">
                  <span>Brightness</span><span data-testid="brightness-value">{brightness}%</span>
                </div>
                <input type="range" min="5" max="100" value={brightness} onChange={(e) => setBrightness(Number(e.target.value))}
                  aria-label="Brightness" data-testid="brightness-slider" className="mt-2 w-full accent-pink" />
                <p className="mt-3 text-center font-display text-xl font-medium text-navy" data-testid="glow-name">{activeLight.name}</p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.12} className="order-1 lg:order-1">
            <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.35em] text-pinkdeep">
              <Moon size={13} /> Better than a night-light
            </p>
            <h2 className="mt-6 font-display text-4xl font-medium leading-[1.02] text-navy sm:text-5xl">
              Afraid of the dark? Meet something better than a{" "}
              <span className="text-gradient-dream">night-light.</span>
            </h2>
            <p className="mt-6 text-base leading-relaxed text-navy/60">A traditional night-light sits across the room.</p>
            <p className="mt-1.5 font-display text-xl font-medium text-pinkdeep sm:text-2xl">Their Buddy glows right beside them.</p>
            <p className="mt-5 max-w-lg text-base leading-relaxed text-navy/60">
              The glow is soft, diffused and comforting — never bright. Pick a color, set the brightness, and let the night feel a little friendlier.
            </p>
            <div className="mt-9 grid grid-cols-3 gap-4">
              {[[Heart, "#FBE7EF", "#E56BB0", "A calmer bedtime routine"],
                [Moon, "#EEE8FF", "#8B76D9", "A sense of security nearby"],
                [Star, "#FBE7EF", "#E56BB0", "A brighter tomorrow"]].map(([Icon, bg, fg, label], i) => (
                <div key={i} className="flex items-center gap-3" data-testid={`nightlight-perk-${i}`}>
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full" style={{ background: bg, color: fg }}>
                    <Icon size={18} />
                  </span>
                  <p className="text-[13px] font-semibold leading-tight text-navy/70">{label}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}

export function HomeStorySections() {
  const navigate = useNavigate();
  return (
    <>
      <CompareSection />
      <NightLightSection />
      {/* App section */}
      <section className="px-4 py-20 sm:px-6 lg:py-28" data-testid="app-section">
        <div className="mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-2 lg:gap-16">
          <Reveal className="order-2 lg:order-1">
            <div className="relative overflow-hidden rounded-[2.5rem] shadow-[0_24px_70px_rgba(16,42,86,0.14)]">
              <img src="/assets/app-parent.png" alt="Parent using the Bedtime Buddies app beside their sleeping child" className="aspect-square h-full w-full object-cover" data-testid="app-image" />
              <p className="absolute left-7 top-7 font-script text-2xl font-semibold leading-tight text-white drop-shadow-lg">
                Peaceful bedtimes<br />in your hands <span className="text-pink">♡</span>
              </p>
              <div className="absolute bottom-7 left-7 flex items-center gap-3 rounded-2xl bg-night/80 px-5 py-4 backdrop-blur-sm">
                <Moon size={22} className="text-lavdeep" fill="#C8B5FF" />
                <p className="text-[11px] font-bold uppercase tracking-[0.2em] leading-snug text-white/85">Small controls.<br />Big tomorrows.</p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.12} className="relative order-1 lg:order-2">
            <Moon className="absolute -top-6 right-0 h-12 w-12 rotate-[15deg] text-moon/50" fill="rgba(255,216,107,0.35)" aria-hidden="true" />
            <Star className="absolute right-14 top-16 h-4 w-4 text-pink" fill="#F58CC8" aria-hidden="true" />
            <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.35em] text-pinkdeep">
              <Moon size={13} /> The parent app
            </p>
            <h2 className="mt-5 font-display text-3xl font-medium leading-[1.05] text-navy sm:text-4xl lg:text-[2.9rem]">
              A Smarter, Calmer Bedtime — For Both of You.
            </h2>
            <p className="mt-5 max-w-lg text-base leading-relaxed text-navy/65">
              Control the glow, stories, sounds, and sleep timer right from your phone — so they can dream bigger, and you can breathe easier.
            </p>
            <div className="mt-8 grid gap-x-8 gap-y-6 sm:grid-cols-2">
              {[
                [Lightbulb, "#FBE7EF", "#E56BB0", "Control the Glow", "Adjust the color and brightness."],
                [BookOpen, "#EEE8FF", "#8B76D9", "Play Stories & Soothing Sounds", "Calming audio, whenever they need it."],
                [Clock, "#DFF3FF", "#4A9ECC", "Set a Sleep Timer", "Lights and audio turn off automatically."],
                [Smartphone, "#FBE7EF", "#E56BB0", "Manage It All From Your Phone", "Simple. Intuitive. Designed for busy parents."],
              ].map(([Icon, bg, fg, title, sub], i) => (
                <div key={i} className="flex items-start gap-3.5" data-testid={`app-feature-${i}`}>
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full" style={{ background: bg, color: fg }}>
                    <Icon size={20} />
                  </span>
                  <div>
                    <p className="font-body text-[15px] font-extrabold leading-tight text-navy">{title}</p>
                    <p className="mt-1 text-sm leading-snug text-navy/55">{sub}</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-8 font-script text-3xl font-semibold text-pinkdeep">You set the magic. They feel the love. ♡</p>
            <Link to="/how-it-works" data-testid="app-learn-cta"
              className="mt-7 inline-flex items-center gap-2 rounded-full bg-navy px-8 py-4 font-body text-base font-bold leading-none text-white transition hover:scale-[1.04]">
              Explore the App <ArrowRight size={17} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* For parents */}
      <section className="relative overflow-hidden bg-cream" data-testid="for-parents-section">
        <img src="/assets/moon-bg2.webp" alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover" />
        <div className="relative mx-auto w-full max-w-6xl space-y-4 px-6 pb-12 pt-20 lg:pb-16 lg:pt-28">
          <Reveal className="pb-6 text-center lg:pb-8">
            <p className="flex items-center justify-center gap-2 text-xs font-bold uppercase tracking-[0.35em] text-pinkdeep">
              <Moon size={13} /> For parents, plainly
            </p>
            <h2 className="mx-auto mt-5 max-w-2xl font-display text-3xl font-medium leading-[1.06] text-navy sm:text-4xl lg:text-5xl" data-testid="for-parents-headline">
              The bedtime questions every parent asks.
            </h2>
            <p className="mt-4 font-script text-2xl font-semibold text-navy/60 sm:text-3xl">Honest answers, no fine print. ♡</p>
          </Reveal>
          {PARENT_CARDS.map((c, i) => (
            <Reveal key={c.num} delay={i * 0.05} y={20}>
              <div className="rounded-[1.75rem] px-8 py-6 sm:px-10 sm:py-7" style={{ background: c.bg }} data-testid={`parent-card-${c.num}`}>
                <div className="flex items-baseline gap-3.5">
                  <span className="text-xs font-bold uppercase tracking-[0.25em] text-navy/35">{c.num}</span>
                  <h3 className="font-display text-xl font-medium text-navy sm:text-2xl">{c.title}</h3>
                </div>
                <p className="mt-2 text-[15px] leading-relaxed text-navy/60 sm:text-base">{c.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* A hug that stays */}
      <section className="px-4 pb-14 pt-8 sm:px-6 lg:pb-20 lg:pt-10" data-testid="hug-section">
        <Reveal>
          <div className="mx-auto max-w-7xl overflow-hidden rounded-[2.5rem] bg-blush lg:grid lg:grid-cols-5">
            <div className="flex flex-col justify-center p-10 sm:p-14 lg:col-span-2">
              <Eyebrow>Not an ordinary plush</Eyebrow>
              <h2 className="mt-5 font-display text-3xl font-medium leading-[1.05] text-navy sm:text-4xl lg:text-[2.75rem]">A hug that stays after you leave.</h2>
              <p className="mt-5 max-w-md text-base leading-relaxed text-navy/65">
                More than a plush. A comforting companion designed to help bedtime feel warmer, softer, and more familiar.
              </p>
              <button onClick={() => navigate("/buddy/luna-the-unicorn")} data-testid="hug-shop-cta"
                className="mt-8 inline-flex w-fit items-center gap-2 rounded-full bg-pink px-7 py-3.5 font-body text-base font-bold leading-none text-navy transition hover:scale-[1.04] hover:bg-moon">
                Shop the Buddies <ArrowRight size={17} />
              </button>
            </div>
            <div className="lg:col-span-3">
              <img src="/assets/hug.png" alt="Luna the Unicorn lying flat with callouts: extra-long body for bigger cuddles, softly weighted for a real hug, calming glow"
                className="h-full w-full object-cover" data-testid="hug-image" />
            </div>
          </div>
        </Reveal>
      </section>

      {/* Benefits */}
      <section className="px-6 py-20 lg:py-28" data-testid="benefits-section">
        <div className="mx-auto max-w-6xl">
          <Reveal className="max-w-2xl">
            <Eyebrow>Why families love them</Eyebrow>
            <h2 className="mt-5 font-display text-3xl font-medium text-navy sm:text-4xl">Everything we love about bedtime, wrapped into one big hug.</h2>
          </Reveal>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {BENEFITS.map((b, i) => (
              <Reveal key={b.title} delay={(i % 3) * 0.08}>
                <div className="card-soft h-full p-8" data-testid={`benefit-${b.title.toLowerCase().replace(/ /g, "-")}`}>
                  <Star size={19} style={{ color: b.color }} fill={b.color} />
                  <h3 className="mt-4 font-display text-lg font-semibold text-navy">{b.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-navy/55">{b.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Meet the Buddies */}
      {/* Product listing removed — buying happens on product pages */}

      {/* How it works */}
      <section className="relative overflow-hidden px-6 py-24 lg:py-32" data-testid="how-it-works-section">
        <img src="/assets/night-sky.png" alt="" aria-hidden="true" className="absolute inset-0 hidden h-full w-full object-cover lg:block" />
        <img src="/assets/how-it-works-mobile.png" alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover lg:hidden" data-testid="how-it-works-bg-mobile" />
        <div className="absolute inset-0 bg-navy/40 lg:bg-navy/20" aria-hidden="true" />
        <div className="relative mx-auto max-w-6xl">
          <Reveal className="max-w-xl">
            <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.3em] text-moon">
              <Moon size={13} /> How it works
            </p>
            <h2 className="mt-5 font-display text-3xl font-medium text-white sm:text-4xl lg:text-[2.75rem]">A calmer bedtime in four simple steps.</h2>
          </Reveal>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {HOW_IT_WORKS.map((c, i) => {
              const pastel = ["#FBE7EF", "#FFF3D1", "#DFF3FF", "#EEE8FF"][i];
              const glowShadow = ["rgba(245,140,200,0.45)", "rgba(255,216,107,0.45)", "rgba(158,220,255,0.45)", "rgba(200,181,255,0.45)"][i];
              return (
                <Reveal key={c.step} delay={i * 0.08}>
                  <motion.div whileHover={{ y: -5 }} className="h-full rounded-[2rem] p-8 backdrop-blur-sm"
                    style={{ background: pastel, boxShadow: `0 18px 55px ${glowShadow}` }}
                    data-testid={`how-card-${c.step.toLowerCase()}`}>
                    <span className="font-display text-sm font-semibold text-navy/40">{c.num}</span>
                    <h3 className="mt-2 font-display text-2xl font-medium tracking-wide text-navy">{c.step}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-navy/60">{c.desc}</p>
                  </motion.div>
                </Reveal>
              );
            })}
          </div>
          <Reveal className="mt-12 text-center">
            <Link to="/buddy/luna-the-unicorn" data-testid="how-find-buddy-cta"
              className="inline-flex items-center gap-2 rounded-full bg-pink px-8 py-4 font-body text-base font-bold leading-none text-navy shadow-xl transition hover:scale-[1.04] hover:bg-moon">
              Find Their Buddy <ArrowRight size={18} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Bedtime routine (pain point) */}
      <section className="relative overflow-hidden bg-cream px-4 py-20 sm:px-6 lg:py-28" data-testid="pain-point-section">
        <img src="/assets/clouds2.webp" alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover" />
        <Star className="absolute left-8 top-12 h-5 w-5 text-moon" fill="#FFD86B" aria-hidden="true" />
        <Star className="absolute right-10 top-10 h-6 w-6 text-pink" fill="#F58CC8" aria-hidden="true" />
        <Reveal className="relative mx-auto max-w-6xl">
          <div className="grid overflow-hidden rounded-[3rem] bg-white/95 shadow-[0_30px_80px_rgba(16,42,86,0.12)] backdrop-blur-sm lg:grid-cols-2">
            <div className="relative min-h-[320px] lg:min-h-[560px]">
              <img src="/assets/doorway.png" alt="Little one in star pajamas standing in a warm doorway, holding a stuffed bunny"
                className="absolute inset-0 h-full w-full object-cover" data-testid="pain-point-image" />
            </div>
            <div className="relative px-8 py-12 text-center sm:px-12 lg:px-14 lg:py-16 lg:text-left">
              <Moon className="absolute right-10 top-8 h-10 w-10 rotate-[-15deg] text-blush" fill="#FBE7EF" aria-hidden="true" />
              <Star className="absolute right-24 top-16 h-3.5 w-3.5 text-moon" fill="#FFD86B" aria-hidden="true" />
              <Heart className="absolute right-8 bottom-28 h-6 w-6 rotate-[15deg] text-pink" aria-hidden="true" />
              <Star className="absolute left-8 bottom-10 h-4 w-4 text-pink" fill="#F58CC8" aria-hidden="true" />

              <p className="flex items-center justify-center gap-2 text-xs font-bold uppercase tracking-[0.35em] text-pinkdeep lg:justify-start">
                <Moon size={13} /> The 8pm Question
              </p>
              <h2 className="mt-6 font-display text-4xl font-medium leading-[1.02] text-navy sm:text-5xl">
                “Can you stay until I fall asleep?”
              </h2>
              <div className="mt-7 space-y-1.5 text-base leading-relaxed text-navy/55">
                <p>The hallway light stays on. The closet gets checked.</p>
                <p>One more question. One more hug. One more story.</p>
                <p>And then: <span className="font-semibold text-pinkdeep">“I don't want to sleep by myself.”</span></p>
              </div>
              <p className="mt-7 text-[15px] leading-relaxed text-navy/50">
                For many kids, nighttime is when everything gets quieter — and their imagination gets louder. Bedtime Buddies weren't made to replace Mom or Dad.
              </p>
              <div className="mt-9 rounded-[2rem] bg-blush px-8 py-6 text-center">
                <p className="font-display text-xl font-medium leading-snug text-navy sm:text-2xl">
                  They leave a little comfort behind when you walk out of the room.
                </p>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* Stories */}
      <section className="bg-white px-6 py-20 lg:py-28" data-testid="stories-section">
        <div className="mx-auto max-w-6xl">
          <Reveal className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <Eyebrow>Storytime, continued</Eyebrow>
              <h2 className="mt-5 font-display text-3xl font-medium text-navy sm:text-4xl">Bedtime stories for brighter tomorrows.</h2>
            </div>
            <Link to="/stories" data-testid="stories-explore-cta" className="group flex items-center gap-2 font-display font-semibold text-pinkdeep transition hover:text-navy">
              Explore All Stories <ArrowRight size={17} className="transition group-hover:translate-x-1" />
            </Link>
          </Reveal>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {STORY_CARDS.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.08}>
                <Link to="/stories" data-testid={`story-home-${i}`} className="group block overflow-hidden rounded-[2rem] bg-white shadow-[0_10px_40px_rgba(16,42,86,0.07)]">
                  <div className="overflow-hidden">
                    <img src={`/assets/${s.img}.png`} alt={`${s.title} story artwork`} loading="lazy"
                      className="aspect-square w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="p-6">
                    <BookOpen size={17} className="text-pink" />
                    <p className="mt-3 font-display text-lg font-medium leading-snug text-navy">{s.title}</p>
                    <div className="mt-2 flex items-end justify-between gap-3">
                      <p className="text-sm leading-relaxed text-navy/55">{s.desc}</p>
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blush text-pinkdeep transition group-hover:bg-pink group-hover:text-navy">
                        <ArrowRight size={16} />
                      </span>
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-10">
            <div className="overflow-hidden rounded-[2.5rem] bg-white shadow-[0_20px_60px_rgba(16,42,86,0.10)]" data-testid="personalized-stories-teaser">
              <div className="relative">
                <img src="/assets/castle.webp" alt="A little girl flying through clouds on Luna the unicorn toward a glowing castle"
                  className="h-64 w-full object-cover sm:h-80" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent" aria-hidden="true" />
              </div>
              <div className="-mt-10 relative px-8 pb-10 text-center">
                <span className="inline-flex rounded-full bg-moon px-4 py-1.5 font-body text-xs font-bold text-navy shadow">PERSONALIZED STORIES — COMING SOON</span>
                <p className="mx-auto mt-5 max-w-xl font-display text-xl font-medium italic leading-relaxed text-navy/75 sm:text-2xl">
                  “Tonight, Luna and Halo discovered a secret castle hidden above the clouds…”
                </p>
                <p className="mt-4 font-script text-2xl font-semibold text-pinkdeep">Imagine your child becoming part of the story.</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
